import { useState, useRef, useCallback, useEffect } from "react";

export interface TapEditTextProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
  label?: string;
}

export function TapEditText({
  value,
  onChange,
  placeholder,
  maxLength,
  disabled = false,
  label,
}: TapEditTextProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const displayRef = useRef<HTMLSpanElement>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  useEffect(() => {
    if (!editing && cancelledRef.current && displayRef.current) {
      displayRef.current.focus();
      cancelledRef.current = false;
    }
  }, [editing]);

  const commit = useCallback(
    (draftValue: string) => {
      const next = draftValue.trim();
      setEditing(false);
      setDraft("");
      onChange(next);
    },
    [onChange],
  );

  const cancel = useCallback(() => {
    setEditing(false);
    setDraft("");
  }, []);

  const startEdit = useCallback(() => {
    cancelledRef.current = false;
    setDraft(value);
    setEditing(true);
  }, [value]);

  const handleClick = useCallback(() => {
    startEdit();
  }, [startEdit]);

  const handleDisplayKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLSpanElement>) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        startEdit();
      }
    },
    [startEdit],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setDraft(e.target.value);
    },
    [],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        commit(draft);
      } else if (e.key === "Escape") {
        e.preventDefault();
        cancelledRef.current = true;
        cancel();
      }
    },
    [draft, commit, cancel],
  );

  const handleBlur = useCallback(() => {
    if (cancelledRef.current) {
      return;
    }
    commit(draft);
  }, [draft, commit]);

  const rootStyle: React.CSSProperties = disabled
    ? { opacity: 0.5, pointerEvents: "none", cursor: "not-allowed" }
    : {};

  const fieldLabel = label ?? "Text";
  const ariaLabel = editing
    ? "Editing " + fieldLabel + ". Press Enter to save, Escape to cancel."
    : fieldLabel + ". Press Enter to edit. Current value: " + (value || "empty");

  return (
    <div
      style={rootStyle}
      role="group"
      aria-label={fieldLabel}
      aria-disabled={disabled || undefined}
    >
      {label && (
        <div
          style={{
            font: "500 10px 'JetBrains Mono', monospace",
            letterSpacing: ".16em",
            textTransform: "uppercase",
            color: "var(--paper-ink-2)",
            marginBottom: "4px",
          }}
        >
          {label}
        </div>
      )}
      {editing ? (
        <input
          ref={inputRef}
          type="text"
          value={draft}
          maxLength={maxLength}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          aria-label={ariaLabel}
          placeholder={placeholder}
          style={{
            font: "300 16px 'Fraunces', serif",
            padding: "4px 8px",
            outline: "2px solid var(--paper-gold)",
            background: "var(--color-input)",
            border: "1px solid var(--paper-rule)",
            color: "var(--paper-ink)",
            width: "100%",
            boxSizing: "border-box",
          }}
        />
      ) : (
        <span
          ref={displayRef}
          onClick={handleClick}
          onKeyDown={handleDisplayKeyDown}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label={ariaLabel}
          aria-disabled={disabled || undefined}
          style={{
            font: "300 16px 'Fraunces', serif",
            padding: "4px 8px",
            cursor: "text",
            display: "inline-block",
            color: value ? "var(--paper-ink)" : "var(--paper-ink-2)",
            userSelect: "none",
            outline: "none",
          }}
        >
          {value || placeholder}
        </span>
      )}
    </div>
  );
}
